/**
 * Comptes (spécification §2, ADR 0007) : e-mail et mot de passe, sans confirmation par
 * e-mail, session gardée sur l'appareil, mot de passe oublié par e-mail.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

export interface Compte {
  email: string;
}

/** Ce que l'interface attend du service de comptes (Supabase Auth ou démonstration). */
export interface Authentification {
  /** Compte connecté sur cet appareil, ou `null`. */
  compte(): Promise<Compte | null>;
  /**
   * Prévient à chaque connexion ou déconnexion. `reinitialisation` : l'utilisateur arrive par
   * le lien de mot de passe oublié et doit choisir un nouveau mot de passe.
   */
  surChangement(rappel: (compte: Compte | null, reinitialisation: boolean) => void): () => void;
  connexion(email: string, motDePasse: string): Promise<void>;
  creation(email: string, motDePasse: string): Promise<void>;
  /** Envoie le lien de réinitialisation ; ne dit jamais si l'adresse a un compte. */
  demanderLien(email: string): Promise<void>;
  changerMotDePasse(motDePasse: string): Promise<void>;
  deconnexion(): Promise<void>;
}

/** Erreur de compte, avec un message à montrer tel quel. */
export class ErreurCompte extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ErreurCompte';
  }
}

/** Longueur minimale d'un mot de passe (proposition du design system). */
export const LONGUEUR_MOT_DE_PASSE = 8;

export const MESSAGE_IDENTIFIANTS = 'Adresse e-mail ou mot de passe incorrect.';

/** Message français pour une erreur de Supabase Auth. */
export function messageErreurCompte(erreur: { code?: string; message: string; status?: number }) {
  switch (erreur.code) {
    case 'invalid_credentials':
      return MESSAGE_IDENTIFIANTS;
    case 'user_already_exists':
    case 'email_exists':
      return 'Un compte existe déjà avec cette adresse. Connectez-vous ou réinitialisez le mot de passe.';
    case 'weak_password':
      return `Mot de passe trop faible : au moins ${LONGUEUR_MOT_DE_PASSE} caractères.`;
    case 'same_password':
      return 'Choisissez un mot de passe différent de l’actuel.';
    case 'email_address_invalid':
    case 'validation_failed':
      return 'Saisissez une adresse e-mail valide, par exemple vous@exemple.fr.';
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit':
      return 'Trop de tentatives. Réessayez dans quelques minutes.';
    case 'signup_disabled':
      return 'La création de compte est fermée.';
  }
  if (erreur.status === 0 || /fetch|network/i.test(erreur.message)) {
    return 'Connexion impossible. Vérifiez votre accès à internet, puis réessayez.';
  }
  return 'Une erreur est survenue. Réessayez dans un instant.';
}

export function creerAuthentificationSupabase(client: SupabaseClient): Authentification {
  async function verifier(promesse: Promise<{ error: unknown }>) {
    const { error } = await promesse;
    if (error) throw new ErreurCompte(messageErreurCompte(error as { message: string }));
  }
  return {
    async compte() {
      const { data } = await client.auth.getSession();
      const email = data.session?.user.email;
      return email === undefined ? null : { email };
    },
    surChangement(rappel) {
      const { data } = client.auth.onAuthStateChange((evenement, session) => {
        const email = session?.user.email;
        rappel(email === undefined ? null : { email }, evenement === 'PASSWORD_RECOVERY');
      });
      return () => data.subscription.unsubscribe();
    },
    connexion: (email, motDePasse) =>
      verifier(client.auth.signInWithPassword({ email, password: motDePasse })),
    creation: (email, motDePasse) => verifier(client.auth.signUp({ email, password: motDePasse })),
    demanderLien: (email) =>
      verifier(
        client.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + window.location.pathname,
        }),
      ),
    changerMotDePasse: (motDePasse) => verifier(client.auth.updateUser({ password: motDePasse })),
    deconnexion: () => verifier(client.auth.signOut()),
  };
}

/** Comptes en mémoire, pour les tests de bout en bout : rien n'est enregistré. */
export function creerAuthentificationMemoire(): Authentification {
  const comptes = new Map<string, string>();
  let connecte: string | null = null;
  const rappels = new Set<(compte: Compte | null, reinitialisation: boolean) => void>();
  const prevenir = () => {
    const compte = connecte === null ? null : { email: connecte };
    for (const rappel of rappels) rappel(compte, false);
  };
  const normaliser = (email: string) => email.trim().toLowerCase();
  return {
    async compte() {
      return connecte === null ? null : { email: connecte };
    },
    surChangement(rappel) {
      rappels.add(rappel);
      return () => rappels.delete(rappel);
    },
    async connexion(email, motDePasse) {
      if (comptes.get(normaliser(email)) !== motDePasse)
        throw new ErreurCompte(MESSAGE_IDENTIFIANTS);
      connecte = normaliser(email);
      prevenir();
    },
    async creation(email, motDePasse) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        throw new ErreurCompte(messageErreurCompte({ code: 'email_address_invalid', message: '' }));
      }
      if (comptes.has(normaliser(email))) {
        throw new ErreurCompte(messageErreurCompte({ code: 'user_already_exists', message: '' }));
      }
      comptes.set(normaliser(email), motDePasse);
      connecte = normaliser(email);
      prevenir();
    },
    async demanderLien() {},
    async changerMotDePasse(motDePasse) {
      if (connecte !== null) comptes.set(connecte, motDePasse);
      prevenir();
    },
    async deconnexion() {
      connecte = null;
      prevenir();
    },
  };
}
